import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getEffectiveLang } from "@/i18n";
import i18n from "@/i18n";
import { navLinks } from "@/mocks/homeData";
import { regions, type RegionData, langLabelMap } from "@/mocks/regions";

interface NavbarProps {
  onOpenModal?: (type: string) => void;
  onOpenRegionSelector?: () => void;
  onOpenQuote?: () => void;
}

function getSavedRegion(): RegionData | undefined {
  const saved = sessionStorage.getItem("openlab-region");
  if (!saved) return undefined;
  try {
    const parsed = JSON.parse(saved);
    return regions.find((r) => r.code === parsed.code);
  } catch {
    return undefined;
  }
}

export default function Navbar({ onOpenModal, onOpenRegionSelector, onOpenQuote }: NavbarProps) {
  const { t } = useTranslation("common");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [currentRegion, setCurrentRegion] = useState<RegionData>(() => {
    const saved = getSavedRegion();
    return saved || regions.find((r) => r.code === "GLOBAL")!;
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Listen for region changes from the RegionSelector modal
  useEffect(() => {
    const handleRegionChange = () => {
      const saved = getSavedRegion();
      if (saved && saved.code !== currentRegion.code) {
        setCurrentRegion(saved);
      }
    };
    window.addEventListener("openlab-region-changed", handleRegionChange);
    return () => window.removeEventListener("openlab-region-changed", handleRegionChange);
  }, [currentRegion.code]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    setActiveDropdown(null);
    if (!href || href === "#") return;
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: href } });
    }
  };

  const handleOpenModal = (type: string) => {
    setActiveDropdown(null);
    setMobileOpen(false);
    if (isHome) {
      onOpenModal?.(type);
    } else {
      navigate("/", { state: { openModal: type } });
    }
  };

  const handleOpenRegionSelector = () => {
    setRegionDropdownOpen(false);
    setMobileOpen(false);
    if (isHome) {
      onOpenRegionSelector?.();
    } else {
      navigate("/", { state: { openRegion: true } });
    }
  };

  const handleQuote = () => {
    setMobileOpen(false);
    if (onOpenQuote) {
      onOpenQuote();
    } else {
      navigate("/", { state: { openQuote: true } });
    }
  };

  const changeRegion = async (region: RegionData) => {
    const effectiveLang = getEffectiveLang(region.lang);
    await i18n.changeLanguage(effectiveLang);
    sessionStorage.setItem(
      "openlab-region",
      JSON.stringify({ code: region.code, lang: region.lang, effectiveLang })
    );
    setCurrentRegion(region);
    setRegionDropdownOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <nav
        className={`transition-all duration-500 ${
          scrolled
            ? "bg-background-900/90 backdrop-blur-2xl border-b border-primary-400/10"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("#hero");
              }}
              className="flex items-center gap-2.5 flex-shrink-0 group"
            >
              <img
                src="https://storage.helloreaddy.io/project_files/b2c15536-355d-426f-a10e-89e210e7256f/284a1860-12a0-47a5-b9c0-f19215b3f5a7_compressed_icono4x.webp"
                alt="Openlab Secure"
                className="h-9 w-auto"
                decoding="async"
                fetchPriority="high"
              />
              <span className="font-heading font-bold text-lg md:text-xl text-foreground-50 tracking-wide">
                Openlab<span className="text-primary-400"> Secure</span>
              </span>
            </a>

            {/* Desktop nav - only at xl+ (1280px+) for tablet safety */}
            <div className="hidden xl:flex items-center gap-0">
              {navLinks.map((link) => (
                <div
                  key={link.labelKey}
                  className="relative"
                  onMouseEnter={() => link.hasDropdown && setActiveDropdown(link.labelKey)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {link.isExternal ? (
                    <Link
                      to={link.isExternal}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground-200/80 hover:text-primary-300 transition-colors duration-300 whitespace-nowrap font-body tracking-wide cursor-pointer nav-underline"
                    >
                      {t(link.labelKey)}
                      <i className="ri-external-link-line text-[10px] opacity-40 ml-0.5" />
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(link.href);
                      }}
                      className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground-200/80 hover:text-primary-300 transition-colors duration-300 whitespace-nowrap font-body tracking-wide cursor-pointer nav-underline"
                    >
                      {t(link.labelKey)}
                      {link.hasDropdown && (
                        <i className="ri-arrow-down-s-line text-xs opacity-60" />
                      )}
                    </a>
                  )}
                  {link.hasDropdown && activeDropdown === link.labelKey && (
                    <div className="absolute top-full left-0 bg-background-900/95 backdrop-blur-2xl rounded-xl border border-primary-400/10 py-2 min-w-[300px] shadow-2xl shadow-primary-500/5">
                      {link.dropdownItems?.map((item) => (
                        item.pageHref ? (
                          <Link
                            key={item.labelKey}
                            to={item.pageHref}
                            onClick={() => setActiveDropdown(null)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-200/70 hover:text-primary-300 hover:bg-primary-500/5 transition-all duration-200 font-body cursor-pointer"
                          >
                            <span className="w-1 h-1 rounded-full bg-primary-400/40" />
                            {t(item.labelKey)}
                          </Link>
                        ) : item.modalType ? (
                          <button
                            key={item.labelKey}
                            type="button"
                            onClick={() => handleOpenModal(item.modalType!)}
                            className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-200/70 hover:text-primary-300 hover:bg-primary-500/5 transition-all duration-200 font-body cursor-pointer"
                          >
                            <span className="w-1 h-1 rounded-full bg-primary-400/40" />
                            {t(item.labelKey)}
                          </button>
                        ) : (
                          <a
                            key={item.labelKey}
                            href={link.href || "#"}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick(link.href);
                              setActiveDropdown(null);
                            }}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-200/70 hover:text-primary-300 hover:bg-primary-500/5 transition-all duration-200 font-body cursor-pointer"
                          >
                            <span className="w-1 h-1 rounded-full bg-primary-400/40" />
                            {t(item.labelKey)}
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right side: visible from lg (1024px+) on tablets */}
            <div className="hidden lg:flex items-center gap-1.5 xl:gap-2">
              {/* Habla con experto */}
              <button
                type="button"
                onClick={handleQuote}
                className="inline-flex items-center px-3 xl:px-5 py-2 xl:py-2.5 rounded-full bg-gradient-to-r from-[#4A7DBF] to-[#3A6DAF] text-white font-body font-semibold text-xs xl:text-sm hover:from-[#5A8DCF] hover:to-[#4A7DBF] transition-all duration-500 whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md"
              >
                <span>{t("nav.quote")}</span>
                <i className="ri-arrow-right-line ml-1.5" />
              </button>

              {/* Support */}
              <a
                href="https://support.openlabsecure.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 xl:px-5 py-2 xl:py-2.5 rounded-full bg-gradient-to-r from-[#6B9BD4] to-[#4A7DBF] text-white font-body font-semibold text-xs xl:text-sm hover:from-[#7AABE4] hover:to-[#5A8DCF] transition-all duration-500 whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md"
                title={t("nav.support")}
              >
                <i className="ri-headphone-line text-sm" />
                <span className="hidden xl:inline">{t("nav.support")}</span>
              </a>

              <div className="w-px h-5 bg-foreground-100/10 mx-0.5" />

              {/* Region selector */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setRegionDropdownOpen(!regionDropdownOpen)}
                  className="flex items-center gap-1 px-1.5 py-2 text-sm font-medium text-foreground-300/70 hover:text-primary-300 transition-colors duration-300 whitespace-nowrap font-body cursor-pointer"
                  title={currentRegion.name}
                >
                  <span className="text-base">{currentRegion.flag}</span>
                  <span className="hidden xl:inline text-xs tracking-wider">{currentRegion.name}</span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${regionDropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {regionDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-background-900/95 backdrop-blur-2xl rounded-xl border border-primary-400/10 py-2 min-w-[280px] max-h-[420px] overflow-y-auto shadow-2xl shadow-primary-500/5 z-50">
                    <p className="px-4 py-2 text-[10px] font-body font-semibold text-foreground-400/40 uppercase tracking-wider">
                      {t("region.region")}
                    </p>
                    {regions.filter((r) => r.code !== "OTHER").map((region) => (
                      <button
                        key={region.code}
                        type="button"
                        onClick={() => changeRegion(region)}
                        className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-primary-500/5 hover:text-primary-300 transition-all duration-200 cursor-pointer ${
                          currentRegion.code === region.code ? "text-primary-300" : "text-foreground-200/70"
                        }`}
                      >
                        <span className="text-base">{region.flag}</span>
                        <span>{region.name}</span>
                        <span className="ml-auto text-[10px] font-body text-foreground-400/40 uppercase">
                          {langLabelMap[region.lang] || region.lang.toUpperCase()}
                        </span>
                      </button>
                    ))}
                    <div className="border-t border-foreground-100/5 mt-1 pt-1">
                      <button
                        type="button"
                        onClick={handleOpenRegionSelector}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-primary-300 hover:bg-primary-500/5 transition-all duration-200 cursor-pointer"
                      >
                        <i className="ri-map-pin-line text-base" />
                        <span>{t("region.change")}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>


            </div>

            {/* Mobile hamburger - visible up to xl (1280px) for tablet support */}
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="xl:hidden p-2 text-foreground-100 hover:text-primary-300 transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 ${mobileOpen ? "rotate-45 translate-y-[9px]" : ""}`} />
                <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 ${mobileOpen ? "opacity-0" : ""}`} />
                <span className={`block h-0.5 bg-current rounded-full transition-all duration-300 ${mobileOpen ? "-rotate-45 -translate-y-[9px]" : ""}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile/Tablet menu - visible up to xl */}
        {mobileOpen && (
          <div className="xl:hidden bg-background-900/95 backdrop-blur-2xl border-t border-primary-400/10">
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <div key={link.labelKey}>
                  {link.isExternal ? (
                    <Link
                      to={link.isExternal}
                      onClick={() => setMobileOpen(false)}
                      className="block px-4 py-3 text-sm font-body text-foreground-200/80 hover:text-primary-300 hover:bg-primary-500/5 rounded-lg transition-all duration-200 cursor-pointer"
                    >
                      {t(link.labelKey)}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(link.href);
                      }}
                      className="block px-4 py-3 text-sm font-body text-foreground-200/80 hover:text-primary-300 hover:bg-primary-500/5 rounded-lg transition-all duration-200 cursor-pointer"
                    >
                      {t(link.labelKey)}
                    </a>
                  )}
                  {link.hasDropdown && link.dropdownItems && (
                    <div className="pl-4 space-y-1">
                      {link.dropdownItems.map((item) => (
                        item.pageHref ? (
                          <Link
                            key={item.labelKey}
                            to={item.pageHref}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-2 px-4 py-2 text-xs font-body text-foreground-400/50 hover:text-primary-300 hover:bg-primary-500/5 rounded-lg transition-all duration-200 cursor-pointer"
                          >
                            <span className="w-1 h-1 rounded-full bg-primary-400/30" />
                            {t(item.labelKey)}
                          </Link>
                        ) : item.modalType ? (
                          <button
                            key={item.labelKey}
                            type="button"
                            onClick={() => handleOpenModal(item.modalType!)}
                            className="w-full text-left flex items-center gap-2 px-4 py-2 text-xs font-body text-foreground-400/50 hover:text-primary-300 hover:bg-primary-500/5 rounded-lg transition-all duration-200 cursor-pointer"
                          >
                            <span className="w-1 h-1 rounded-full bg-primary-400/30" />
                            {t(item.labelKey)}
                          </button>
                        ) : (
                          <a
                            key={item.labelKey}
                            href={link.href || "#"}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick(link.href);
                              setMobileOpen(false);
                            }}
                            className="flex items-center gap-2 px-4 py-2 text-xs font-body text-foreground-400/50 hover:text-primary-300 hover:bg-primary-500/5 rounded-lg transition-all duration-200 cursor-pointer"
                          >
                            <span className="w-1 h-1 rounded-full bg-primary-400/30" />
                            {t(item.labelKey)}
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Mobile CTA buttons */}
              <div className="pt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={handleQuote}
                  className="text-center px-4 py-3 rounded-full bg-gradient-to-r from-[#4A7DBF] to-[#3A6DAF] text-white font-body font-semibold text-sm cursor-pointer whitespace-nowrap"
                >
                  {t("nav.quote")}
                </button>
                <a
                  href="https://support.openlabsecure.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="text-center px-4 py-3 rounded-full bg-gradient-to-r from-[#6B9BD4] to-[#4A7DBF] text-white font-body font-semibold text-sm cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-headphone-line mr-1" />
                  {t("nav.support")}
                </a>
              </div>

              {/* Mobile region selector */}
              <div className="px-4 py-3 border-t border-foreground-100/5 mt-2">
                <p className="text-[10px] font-body text-foreground-400/40 uppercase tracking-wider mb-2">
                  {t("region.region")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {regions.filter((r) => r.code !== "OTHER").map((region) => (
                    <button
                      key={region.code}
                      type="button"
                      onClick={() => {
                        changeRegion(region);
                        setMobileOpen(false);
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-body transition-all duration-200 cursor-pointer ${
                        currentRegion.code === region.code
                          ? "bg-primary-500/15 text-primary-300 border border-primary-400/20"
                          : "bg-background-900/40 text-foreground-300/60 border border-foreground-100/5 hover:border-primary-400/20"
                      }`}
                    >
                      <span>{region.flag}</span>
                      <span className="truncate">{region.name}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={handleOpenRegionSelector}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-body text-primary-300 border border-primary-400/20 bg-primary-500/10 transition-all duration-200 cursor-pointer col-span-2"
                  >
                    <i className="ri-map-pin-line" />
                    <span>{t("region.change")}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}